import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import type { ApprovedDomain, ApprovedDomainRecord } from '@triedge/staff-settings-domain';
import { Card, SectionHeader, Input, Button, Icon } from '../../components/ui';
import { useYstReducedMotion } from '../../../../components/motion/ystMotion';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  useAddApprovedDomainMutation,
  useListApprovedDomainsQuery,
  useRemoveApprovedDomainMutation,
} from '../../../../state/domainRouterApi';
import { useStaffSettingsState } from '../../hooks/useStaffSettingsState';
import {
  isValidApprovedDomain,
  readStaffProvisioningState,
} from '../../utils/staffProvisioning';

type DomainRow = {
  domain: string;
  addedAt: string;
  addedBy: string;
};

const toText = (value: unknown): string => String(value ?? '').trim();
const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => entry as Record<string, unknown>)
    : [];

const toApprovedDomainSource = (
  value: unknown
): Pick<ApprovedDomainRecord, 'domain' | 'addedAt' | 'addedBy'> | null => {
  const record = asRecord(value);
  const domain = toText(record.domain).toLowerCase();
  if (!domain) return null;
  return {
    domain,
    addedAt: toText(record.addedAt),
    addedBy: toText(record.addedBy),
  };
};

const normalizeDomains = (items: readonly ApprovedDomain[] | undefined): DomainRow[] =>
  [...(items ?? [])]
    .map((row) => ({
      domain: toText(row.domain),
      addedAt: toText(row.addedAt),
      addedBy: toText(row.addedBy),
    }))
    .sort((left, right) => left.domain.localeCompare(right.domain));

const normalizeDomainRecords = (
  items: readonly Pick<ApprovedDomainRecord, 'domain' | 'addedAt' | 'addedBy'>[] | undefined
): DomainRow[] =>
  [...(items ?? [])]
    .map((row) => ({
      domain: toText(row.domain).toLowerCase(),
      addedAt: toText(row.addedAt),
      addedBy: toText(row.addedBy),
    }))
    .sort((left, right) => left.domain.localeCompare(right.domain));

export default function ApprovedDomainsSection() {
  const reducedMotion = useYstReducedMotion();
  const motionProps = {
    initial: { opacity: 0, y: reducedMotion ? 0 : 6 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reducedMotion ? 0 : 0.18, ease: [0.16, 1, 0.3, 1] as number[] },
  };

  const { settingsState, isLoading: isSettingsStateLoading } = useStaffSettingsState();
  const approvedDomainsQuery = useListApprovedDomainsQuery();
  const [addApprovedDomain, addApprovedDomainState] = useAddApprovedDomainMutation();
  const [removeApprovedDomain, removeApprovedDomainState] = useRemoveApprovedDomainMutation();

  const [newDomain, setNewDomain] = useState('');
  const [operationError, setOperationError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const fallbackDomains = useMemo(
    () => normalizeDomains(readStaffProvisioningState(settingsState).approvedDomains),
    [settingsState]
  );
  const routedDomains = useMemo(
    () => normalizeDomainRecords(
      asRecordArray(asRecord(approvedDomainsQuery.data).items)
        .map((entry) => toApprovedDomainSource(entry))
        .filter((entry): entry is Pick<ApprovedDomainRecord, 'domain' | 'addedAt' | 'addedBy'> => entry !== null)
    ),
    [approvedDomainsQuery.data]
  );
  const [storedDomains, setStoredDomains] = useState<DomainRow[]>(fallbackDomains);

  useEffect(() => {
    if (approvedDomainsQuery.isSuccess) {
      setStoredDomains(routedDomains);
      return;
    }
    setStoredDomains(fallbackDomains);
  }, [approvedDomainsQuery.isSuccess, fallbackDomains, routedDomains]);

  const domains = storedDomains;
  const isLoading =
    !approvedDomainsQuery.isSuccess
    && domains.length === 0
    && (approvedDomainsQuery.isLoading || isSettingsStateLoading);
  const isSaving = addApprovedDomainState.isLoading || removeApprovedDomainState.isLoading;

  const handleAdd = async () => {
    const trimmed = newDomain.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidApprovedDomain(trimmed)) {
      setValidationError('Enter a valid domain, e.g. school.edu');
      return;
    }
    if (domains.some((row) => row.domain === trimmed)) {
      setValidationError('This domain is already on the approved list.');
      return;
    }
    setValidationError('');
    setOperationError('');
    try {
      const nextRecord = toApprovedDomainSource(
        await addApprovedDomain({ domain: trimmed }).unwrap()
      );
      if (!nextRecord) {
        throw new Error('addApprovedDomain returned no domain.');
      }
      setStoredDomains((previous) =>
        normalizeDomainRecords([
          ...previous.filter((row) => row.domain !== nextRecord.domain),
          nextRecord,
        ])
      );
      setNewDomain('');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to add domain.';
      setOperationError(msg);
    }
  };

  const handleRemove = async (domain: string) => {
    setOperationError('');
    try {
      await removeApprovedDomain({ domain }).unwrap();
      setStoredDomains((previous) => previous.filter((row) => row.domain !== domain));
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to remove domain.';
      setOperationError(msg);
    }
  };

  return (
    <motion.div {...motionProps}>
      <Card data-testid="settings-users-approved-domains-section">
        <SectionHeader
          title="Approved Email Domains"
          description="Staff whose email matches an approved domain may be auto-approved at signup without a code."
        />

        {operationError ? (
          <SupportErrorMessage className="mb-4">
            {operationError}
          </SupportErrorMessage>
        ) : null}

        <div
          data-testid="settings-users-approved-domains-form"
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start"
        >
          <div className="flex-1">
            <Input
              label=""
              value={newDomain}
              onChange={(event) => {
                setNewDomain(event.target.value);
                setValidationError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleAdd();
              }}
              placeholder="yourdomain.edu"
            />
            {validationError ? (
              <p className="mt-1 text-xs text-strawberry">{validationError}</p>
            ) : null}
          </div>
          <Button
            variant="outline"
            onClick={() => void handleAdd()}
            disabled={isSaving || !newDomain.trim()}
            className="w-full shrink-0 sm:mt-0 sm:w-auto"
          >
            <Icon name="Plus" className="mr-1 h-4 w-4" />
            {isSaving ? 'Saving…' : 'Add Domain'}
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-text-secondary">Loading domains…</p>
        ) : domains.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted px-4 py-6 text-center text-sm text-text-secondary">
            No approved domains configured. Staff will require a signup code to register.
          </div>
        ) : (
          <div
            data-testid="settings-users-approved-domains-list"
            className="flex flex-wrap gap-2"
          >
            {domains.map((row) => (
              <span
                key={row.domain}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-sm font-medium text-irongrey"
              >
                <Icon name="Globe" className="h-3.5 w-3.5 text-text-tertiary" />
                <span className="min-w-0 break-all">{row.domain}</span>
                <button
                  data-testid={`settings-users-approved-domain-remove-${row.domain}`}
                  className="ml-1 text-text-tertiary transition-colors hover:text-strawberry"
                  disabled={isSaving}
                  onClick={() => void handleRemove(row.domain)}
                  aria-label={`Remove ${row.domain}`}
                >
                  <Icon name="X" className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Card>
    </motion.div>
  );
}
